/**
 * useDrafts hook
 * Manages draft check-ins in AsyncStorage
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { useState, useCallback, useEffect } from 'react';

/**
 * Generate a simple unique ID for drafts
 */
function generateDraftId(): string {
  return `draft_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Per-entry draft data for multi-entry check-ins
 */
export interface CheckInDraftEntry {
  menuItem: { id: string; name: string } | null;
  ratingId: number | null;
  photos: string[]; // Local URIs
  reviewContent: string;
}

/**
 * Check-in draft structure.
 * Legacy single-entry fields (menuItem, ratingId, photos, reviewContent) are kept
 * for backward compatibility. The `entries` array is the canonical source when present.
 */
export interface CheckInDraft {
  id: string; // Local UUID
  checkInId: string | null; // Backend ID if draft was synced
  business: { id: string; name: string } | null;
  /** @deprecated Use entries[0] instead. Kept for backward compat with old drafts. */
  menuItem: { id: string; name: string } | null;
  /** @deprecated Use entries[0] instead. */
  ratingId: number | null;
  diningType: 'dine_in' | 'takeout';
  /** @deprecated Use entries[0] instead. */
  photos: string[]; // Local URIs
  /** @deprecated Use entries[0] instead. */
  reviewContent: string;
  /** Multi-entry data. When present, takes precedence over legacy fields. */
  entries?: CheckInDraftEntry[];
  createdAt: string; // ISO timestamp
  updatedAt: string; // ISO timestamp
}

// Storage key
const STORAGE_KEY = '@flavrly/check-in-drafts';

/**
 * Get all drafts from AsyncStorage
 */
export async function getDraftsFromStorage(): Promise<CheckInDraft[]> {
  try {
    const draftsJson = await AsyncStorage.getItem(STORAGE_KEY);
    if (!draftsJson) {
      return [];
    }

    const drafts = JSON.parse(draftsJson) as CheckInDraft[];

    // Sort by updatedAt (most recent first)
    return drafts.sort((a, b) =>
      new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
  } catch (error) {
    console.error('Failed to get drafts from storage:', error);
    return [];
  }
}

/**
 * Save a draft to AsyncStorage
 */
export async function saveDraftToStorage(draft: CheckInDraft): Promise<void> {
  try {
    const drafts = await getDraftsFromStorage();

    // Check if draft already exists
    const existingIndex = drafts.findIndex(d => d.id === draft.id);

    if (existingIndex >= 0) {
      // Update existing draft
      drafts[existingIndex] = {
        ...draft,
        updatedAt: new Date().toISOString(),
      };
    } else {
      // Add new draft
      drafts.push({
        ...draft,
        createdAt: draft.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    }

    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
  } catch (error) {
    console.error('Failed to save draft to storage:', error);
    throw error;
  }
}

/**
 * Delete a draft from AsyncStorage
 */
export async function deleteDraftFromStorage(draftId: string): Promise<void> {
  try {
    const drafts = await getDraftsFromStorage();
    const updatedDrafts = drafts.filter(d => d.id !== draftId);
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(updatedDrafts));
  } catch (error) {
    console.error('Failed to delete draft from storage:', error);
    throw error;
  }
}

/**
 * Get a single draft by ID
 */
export async function getDraftByIdFromStorage(draftId: string): Promise<CheckInDraft | null> {
  try {
    const drafts = await getDraftsFromStorage();
    return drafts.find(d => d.id === draftId) || null;
  } catch (error) {
    console.error('Failed to get draft by ID from storage:', error);
    return null;
  }
}

/**
 * Clear all drafts (useful for testing)
 */
export async function clearAllDrafts(): Promise<void> {
  try {
    await AsyncStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear all drafts:', error);
    throw error;
  }
}

/**
 * useDrafts hook
 * Provides stateful access to draft check-ins
 */
export function useDrafts() {
  const [drafts, setDrafts] = useState<CheckInDraft[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDrafts = useCallback(async (): Promise<CheckInDraft[]> => {
    try {
      setIsLoading(true);
      setError(null);
      const loadedDrafts = await getDraftsFromStorage();
      setDrafts(loadedDrafts);
      return loadedDrafts;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load drafts';
      setError(errorMessage);
      console.error('Failed to load drafts:', err);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load drafts on mount
  useEffect(() => {
    loadDrafts();
  }, [loadDrafts]);

  const saveDraft = useCallback(async (draft: Omit<CheckInDraft, 'id' | 'createdAt' | 'updatedAt'> & { id?: string }): Promise<string> => {
    try {
      setError(null);

      // Generate ID if not provided
      const draftId = draft.id || generateDraftId();

      const draftToSave: CheckInDraft = {
        ...draft,
        id: draftId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      await saveDraftToStorage(draftToSave);

      // Reload drafts to update state
      await loadDrafts();

      return draftId;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save draft';
      setError(errorMessage);
      throw err;
    }
  }, [loadDrafts]);

  const deleteDraft = useCallback(async (draftId: string) => {
    try {
      setError(null);
      await deleteDraftFromStorage(draftId);

      // Reload drafts to update state
      await loadDrafts();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete draft';
      setError(errorMessage);
      throw err;
    }
  }, [loadDrafts]);

  const getDraftById = useCallback((draftId: string): CheckInDraft | null => {
    return drafts.find(d => d.id === draftId) || null;
  }, [drafts]);

  const refreshDrafts = useCallback((): Promise<CheckInDraft[]> => {
    return loadDrafts();
  }, [loadDrafts]);

  return {
    drafts,
    isLoading,
    error,
    saveDraft,
    deleteDraft,
    getDraftById,
    refreshDrafts,
  };
}
